<template>
  <v-card class="project-card">
    <div class="project-card__breadcrumb">
      <v-breadcrumbs :items="breadcrumbItems"></v-breadcrumbs>
    </div>
    <v-container class="project-card__top">
      <v-container class="project-card__col" id="project-card__left">
        <div class="card-section" v-if="projectCompany">
          <span
            id="company-name"
            class="overline"
            @click="handleCompanyClick"
            >{{ projectCompany }}</span
          >
          <v-row class="project-card__data-row">
            <v-col class="project-card__data-col" cols="12" sm="12">
              <h1>{{ projectTitle }}</h1>
            </v-col>
          </v-row>
          <v-row class="project-card__data-row">
            <v-col class="project-card__data-col" cols="12" sm="12">
              <span class="subtitle-1 project-subtitle">{{
                projectSubtitle
              }}</span>
            </v-col>
          </v-row>
          <DetailsPanel :data="data" />
        </div>
        <div class="card-section" v-if="projectWebsiteCopy">
          <div class="text-section-header">website copy</div>
          <v-row class="project-card__data-row">
            <v-col
              class="project-card__data-col"
              cols="12"
              sm="12"
              v-html="projectWebsiteCopy"
              >{{
            }}</v-col>
          </v-row>
        </div>
        <div class="card-section" v-if="projectAdditionalCopy">
          <v-row class="project-card__data-row">
            <div class="text-section-header">Additional copy</div>
            <v-col
              class="project-card__data-col body-1"
              cols="12"
              sm="12"
              v-html="projectAdditionalCopy"
              >{{
            }}</v-col>
          </v-row>
        </div>
        <div class="card-section" v-if="projectClientQuote">
          <v-row class="project-card__data-row">
            <div class="text-section-header">Quote</div>
            <v-col class="project-card__data-col" cols="12" sm="12">
              <span>{{ projectClientQuote }}</span>
            </v-col>
          </v-row>
        </div>
        <div class="card-section" v-if="projectQuoteAttiribution">
          <v-row class="project-card__data-row">
            <div class="text-section-header">Quote Attribution</div>
            <v-col class="project-card__data-col" cols="12" sm="12">
              <span> {{ projectQuoteAttiribution }}</span>
            </v-col>
          </v-row>
        </div>
        <RelatedProjects
          v-if="relatedProjects"
          :relatedProjects="relatedProjects"
        />
      </v-container>
      <v-container class="project-card__col" id="project-card__right">
        <ProjectImageGallery :imageDatas="imageDatas" />
        <PrivateImageGallery :imageDatas="privateImageDatas" />
        <LinksPanel :data="data" />
        <TaxonomyPanel :data="data" />
      </v-container>
    </v-container>
    <v-container class="project-card__bottom">
      <v-btn class="link-button" color="primary" @click="handleEditData">
        Edit Data
        <v-icon>mdi-open-in-new</v-icon>
      </v-btn>
    </v-container>
  </v-card>
</template>

<script>
// vuex
import { mapActions } from "vuex";
// library
// internal
// components
import ProjectImageGallery from "@/components/SingleProject/ProjectImageGallery";
import PrivateImageGallery from "@/components/SingleProject/PrivateImageGallery";
import DetailsPanel from "@/components/SingleProject/DetailsPanel";
import LinksPanel from "@/components/SingleProject/LinksPanel";
import TaxonomyPanel from "@/components/SingleProject/TaxonomyPanel";
import RelatedProjects from "@/components/SingleProject/RelatedProjects";
export default {
  name: "ProjectCard",
  components: {
    ProjectImageGallery,
    PrivateImageGallery,
    DetailsPanel,
    TaxonomyPanel,
    RelatedProjects,
    LinksPanel
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    // we'll be using this data a lot - lets make a smaller name for it
    acf() {
      return this.data.acf_data;
    },
    projectTitle() {
      return this.data["post_title"];
    },
    projectCompany() {
      return this.acf["client"];
    },
    projectSubtitle() {
      return this.acf["subtitle"];
    },
    projectImageSrc() {
      return this.acf["thumbnail_image"];
    },
    projectWebsiteCopy() {
      return this.acf["body_copy"];
    },
    projectAdditionalCopy() {
      return this.acf["additional_notes"] === ""
        ? null
        : this.acf["additional_notes"];
    },
    projectClientQuote() {
      return this.acf["client_quote"];
    },
    projectQuoteAttiribution() {
      if (this["name_of_client"] === "") return "";
      if (this.acf["title_of_client"] === "") {
        return this.acf["name_of_client"];
      } else {
        return `${this.acf["name_of_client"]} - ${this.acf["title_of_client"]}`;
      }
    },
    projectClientTitle() {
      return this.acf["title_of_client"];
    },
    relatedProjects() {
      return this.acf["related_projects"];
    },
    imageDatas() {
      const DEFAULT_IMAGE_SIZE = "2048x2048";
      const retval = [];

      let allImagesObjs = [this.acf["primary_image"]];

      if (this.acf["body_image_1"]) {
        allImagesObjs.push(this.acf["body_image_1"]);
      }

      if (this.acf["body_image_2"]) {
        allImagesObjs.push(this.acf["body_image_2"]);
      }

      if (this.acf["additional_images"]) {
        allImagesObjs = [...allImagesObjs, ...this.acf["additional_images"]];
      }

      allImagesObjs.forEach(el => {
        const sizes = Object.keys(el.sizes).filter(
          key => !key.endsWith("-height") && !key.endsWith("-width")
        );

        const sizesObj = {};

        sizes.forEach(size => {
          sizesObj[size] = el.sizes[size];
        });

        retval.push({
          link: el.sizes[DEFAULT_IMAGE_SIZE],
          singleSize: false,
          sizes: sizesObj,
          caption: el.caption
        });
      });

      return retval;
    },
    privateImageDatas() {
      const DEFAULT_IMAGE_SIZE = "2048x2048";
      const retval = [];

      let privateImagesObjs = [];

      if (this.acf["private_gallery_images"]) {
        privateImagesObjs = [
          ...privateImagesObjs,
          ...this.acf["private_gallery_images"]
        ];
      }

      privateImagesObjs.forEach(el => {
        const sizes = Object.keys(el.sizes).filter(
          key => !key.endsWith("-height") && !key.endsWith("-width")
        );

        const sizesObj = {};

        sizes.forEach(size => {
          sizesObj[size] = el.sizes[size];
        });

        retval.push({
          link: el.sizes[DEFAULT_IMAGE_SIZE],
          singleSize: false,
          sizes: sizesObj,
          caption: el.caption
        });
      });

      return retval;
    },
    stamenDotComLink() {
      return `https://stamen.com/work/${this.data["post_name"]}`;
    },
    editDataLink() {
      return `https://stamen.com/wp-admin/post.php?post=${this.data["ID"]}&action=edit`;
    },
    breadcrumbItems() {
      return [
        {
          text: "Archive",
          disabled: false,
          to: "/"
        },
        {
          text: `${this.projectTitle}`,
          disabled: true
        }
      ];
    }
  },
  methods: {
    ...mapActions(["setProjectTextSearch"]),
    handlePublicLink() {
      window.open(this.stamenDotComLink, "_blank");
    },
    handleEditData() {
      window.open(this.editDataLink, "_blank");
    },
    handleCompanyClick() {
      this.$router.push({
        path: "/",
        query: { s: encodeURI(this.projectCompany) }
      });
    }
  }
};
</script>

<style lang="scss">
.project-card {
  max-width: 1350px;
  margin: 0 auto;
  margin-top: 30px;
  margin-bottom: 15px;
  display: flex;
  flex-flow: column;
  position: relative;

  &__top {
    display: flex;
    flex-flow: row;
    position: relative;
    margin: 0 auto;
    padding-top: 0px;

    @media (min-width: 1264px) {
      max-width: 1400px;
    }
  }

  &__bottom {
    display: flex;
    justify-content: flex-end;
    max-width: unset;

    .link-button {
      margin-left: 10px;

      i {
        margin-left: 10px;
      }
    }
  }

  .row {
    margin: unset;
  }

  #project-card__left {
    width: 60%;
    padding: 12px 3px 12px 12px;

    .project-subtitle {
      font-size: 1.25em !important;
    }
  }
  #project-card__right {
    padding-top: 16px;
    width: 40%;
  }

  &__data-col {
    padding: 0px;

    #company-name-column {
    }
  }

  #company-name {
    padding-top: 0px;
    cursor: pointer;
  }
}
</style>
